import styled from "styled-components";
import React, { Fragment } from "react";
import NumberFormat from "react-number-format";
import convertToEnglishNumber from "../../../utils/convertToEnglishNumber";

export interface InputChangeObject {
  value: string;
  eventType: string;
}
export interface InputProps {
  /**
   * What is the input Id?
   */
  id?: string;
  /**
   * What is the input type
   */
  type: "text" | "password" | "tel" | "number" | "email";
  /**
   * What is the input Mode
   */
  inputMode?:
    | "text"
    | "decimal"
    | "numeric"
    | "search"
    | "email"
    | "url"
    | "tel"
    | "none";
  /**
   * What is the input default placeholder
   */
  placeholder?: string;
  /**
   * What action should be done one input change ?
   */
  handleOnChange: (e: InputChangeObject) => void;
  /**
   * What is the input default value?
   */
  defaultValue?: any;
  /**
   * What is the input name
   */
  name?: string;
  /**
   * What is the input direction
   */
  direction?: string;
  readonly?: boolean;
  /**
   * Should Input Be masked?
   */
  maskType?:
    | "DEFAULT"
    | "MASKED"
    | "CARD"
    | "VERIFICATION"
    | "FOURDIGIT"
    | "FIVEDIGIT"
    | "BirthDate"
    | "IBAN"
    | "TELL"
    | "NATIONALID"
    | "TWODIGIT";
  /**
   * Masked Listen To Change?
   */
  listenToChange?: boolean;
  hasError?: boolean;
  maxLength?: number;
  hasDisabled?: boolean;
}

interface FormInputProps {
  direction?: string;
  readonly?: boolean;
  hasError?: boolean;
}

// interface IOutputType {
//   value: any;
//   eventType: any;
// }
/**
 * In Case you Missed Place Id
 */

const generateRandomString = () => {
  return Math.random().toString(36).substring(7);
};
/**
 * Primary UI component for Input
 */
const FormInput = styled.input.attrs({
  placeholderTextColor: "bgRed",
})<FormInputProps>`
  background-color: transparent;
  height: 100%;
  border: solid 1px transparent;
  direction: ${(p) => (p.direction ? p.direction : "inherit")};
  color: ${(theme: any) => theme.theme.bgText};
  /* Chrome, Safari, Edge, Opera */
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type="number"] {
    -moz-appearance: textfield;
  }
  ::-webkit-input-placeholder {
    /* Edge */
    color: ${(theme: any) => theme.theme.bgText};
  }
  &:focus {
    background-color: transparent !important;
    border: ${(p) => (p.hasError ? "none" : "solid 1px #0a84ff")};
    /* outline: 0; */
    box-shadow: none;
    color: ${(theme: any) => theme.theme.bgText} !important;
  }
  &:read-only {
    background-color: transparent !important;
    border-color: transparent;
    outline: 0;
    box-shadow: none;
    opacity: 0.5;
    color: ${(theme: any) => theme.theme.bgText} !important;
  }
`;
const StyledNumberFormat = styled(NumberFormat)<FormInputProps>`
  background-color: transparent;
  height: 100%;
  font-size: 12px;
  border: none;
  direction: ${(p) => (p.direction ? p.direction : "inherit")};
  color: ${(theme: any) => theme.theme.bgText};
  ::-webkit-input-placeholder {
    /* Edge */
    color: ${(theme: any) => theme.theme.bgText};
  }
  &:focus {
    background-color: transparent !important;
    border-color: transparent;
    outline: 0;
    box-shadow: none;
    color: ${(theme: any) => theme.theme.bgText} !important;
  }
  &:read-only {
    background-color: transparent !important;
    border-color: transparent;
    outline: 0;
    box-shadow: none;
    color: ${(theme: any) => theme.theme.bgText} !important;
  }
`;
const StyledCardFormat = styled(NumberFormat)<FormInputProps>`
  background-color: transparent;
  height: 100%;
  font-size: 15px;
  text-align: center;
  border: none;
  direction: ${(p) => (p.direction ? p.direction : "inherit")};
  color: ${(theme: any) => theme.theme.bgText};
  ::-webkit-input-placeholder {
    /* Edge */
    color: ${(theme: any) => theme.theme.bgText};
  }
  &:focus {
    background-color: transparent !important;
    border-color: transparent;
    outline: 0;
    box-shadow: none;
    color: ${(theme: any) => theme.theme.bgText} !important;
  }
  &:read-only {
    background-color: transparent !important;
    border-color: transparent;
    outline: 0;
    box-shadow: none;
    color: ${(theme: any) => theme.theme.bgText} !important;
  }
`;
const Input: React.FC<InputProps> = ({
  id = generateRandomString(),
  type = "tel",
  inputMode = "tel",
  placeholder = "",
  handleOnChange,
  defaultValue,
  name,
  direction,
  readonly = false,
  maskType = "DEFAULT",
  listenToChange,
  hasError = false,
  maxLength = 1000,
  hasDisabled = false,
}) => {
  /**
   * Handle Input change to emmit value
   * @param elem
   */
  const handleInputVal = (elem: any) => {
    const val = convertToEnglishNumber(elem.target.value);
    handleOnChange({
      value: val,
      eventType: elem.type,
    });
  };
  const formatOutput = (e: any) => {
    if (listenToChange) {
      return;
    }
    handleOnChange({
      value: e.floatValue,
      eventType: "mask-change",
    });
  };
  const formatIbanOutput = (e: any) => {
    if (listenToChange) {
      return;
    }
    handleOnChange({
      value: e.value,
      eventType: "mask-change",
    });
  };
  const handleIsChange = (e: any) => {
    if (listenToChange) {
      const c = e.target.value.replace(/[^0-9.-]+/g, "");
      handleOnChange({
        value: c,
        eventType: "mask-change",
      });
    }
  };
  const formatOutputWihZero = (elem: any) => {
    const val = elem.value;
    handleOnChange({
      value: val,
      eventType: "mask-change",
    });
  };
  /**
   * Styled Input Form Control
   */

  return (
    <Fragment>
      {
        {
          DEFAULT: (
            <FormInput
              className="form-control"
              id={id}
              inputMode={inputMode}
              type={type}
              placeholder={placeholder}
              onChange={handleInputVal}
              value={defaultValue}
              name={name}
              direction={direction || "inherit"}
              readOnly={readonly}
              hasError={hasError}
              maxLength={maxLength}
              disabled={hasDisabled}
            />
          ),
          MASKED: (
            <StyledNumberFormat
              className="form-control"
              inputMode={inputMode}
              isNumericString
              //@ts-ignore
              type={type}
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onChange={handleIsChange}
              onValueChange={formatOutput}
              value={defaultValue}
              thousandSeparator={true}
              allowEmptyFormatting={true}
              disabled={hasDisabled}
            />
          ),
          CARD: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutput}
              value={defaultValue}
              format="#### #### #### ####"
              mask="_"
              thousandSeparator={true}
              allowEmptyFormatting={true}
            />
          ),
          VERIFICATION: (
            <StyledCardFormat
              className="form-control"
              id={id}
              allowLeadingZeros={true}
              inputMode={"numeric"}
              type={"text"}
              isNumericString={true}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutputWihZero}
              value={defaultValue}
              format="######"
              mask="_"
              thousandSeparator={true}
              allowEmptyFormatting={true}
              disabled={hasDisabled}
            />
          ),
          FOURDIGIT: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutputWihZero}
              value={defaultValue}
              format="# # # #"
              mask="_"
              thousandSeparator={true}
              allowEmptyFormatting={true}
            />
          ),
          FIVEDIGIT: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutputWihZero}
              value={defaultValue}
              format="# # # # #"
              mask="_"
              thousandSeparator={true}
              readOnly={readonly}
              allowEmptyFormatting={true}
            />
          ),
          TELL: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutput}
              value={defaultValue}
              format="### ########"
              mask="_"
              thousandSeparator={true}
              allowEmptyFormatting={true}
            />
          ),
          BirthDate: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutput}
              value={defaultValue}
              format="####/##/##"
              mask="-"
              thousandSeparator={true}
              allowEmptyFormatting={true}
            />
          ),
          IBAN: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatIbanOutput}
              value={defaultValue}
              format="########################"
              mask="_"
              thousandSeparator={true}
              allowEmptyFormatting={true}
            />
          ),
          NATIONALID: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutputWihZero}
              value={defaultValue}
              format="# # # # # # # # # #"
              mask="_"
              thousandSeparator={true}
              readOnly={readonly}
              allowEmptyFormatting={true}
            />
          ),
          TWODIGIT: (
            <StyledCardFormat
              className="form-control"
              id={id}
              placeholder={placeholder}
              direction={direction || "inherit"}
              onValueChange={formatOutputWihZero}
              value={defaultValue}
              format="# #"
              mask="_"
              thousandSeparator={true}
              readOnly={readonly}
              allowEmptyFormatting={true}
            />
          ),
        }[maskType]
      }
    </Fragment>
  );
};

export default Input;
